import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Wallet } from "lucide-react";
import { api, errMsg, fmtDate, todayISO } from "@/lib/api";
import { Field, EmptyState } from "@/components/Field";

export const CAUSALI = ["Contanti", "Bonifico", "POS / Carta", "Satispay", "Assegno"];
export const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const blank = () => ({ date: todayISO(), tariff: "", amount: "", with_invoice: false, causale: "" });

const IncomeForm = ({ pid, tariffs, onSaved }) => {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);
  const t = tariffs.find((x) => x.key === f.tariff);
  const custom = f.tariff === "custom";
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/patients/${pid}/incomes`, { date: f.date, causale: f.causale, tariff: custom ? null : f.tariff || null,
        amount: custom && f.amount !== "" ? Number(f.amount) : null, with_invoice: custom && f.with_invoice });
      toast.success("Pagamento registrato");
      setF(blank());
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="card space-y-3" data-testid="payment-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Data"><input type="date" required className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="payment-date-input" /></Field>
        <Field label="Tariffa">
          <select className="field" required value={f.tariff} onChange={(e) => setF({ ...f, tariff: e.target.value })} data-testid="payment-tariff-select">
            <option value="">Seleziona…</option>
            {tariffs.map((x) => <option key={x.key} value={x.key}>{x.label} – {eur(x.amount)}</option>)}
            <option value="custom">Importo personalizzato</option>
          </select>
        </Field>
        <Field label="Causale">
          <input list="causali-list" className="field" value={f.causale} onChange={(e) => setF({ ...f, causale: e.target.value })} placeholder="Es. Contanti, Bonifico, POS" data-testid="payment-causale-input" />
          <datalist id="causali-list">{CAUSALI.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
      </div>
      {t && <p className="text-xs text-[#2f4f47]" data-testid="payment-tariff-breakdown">{t.invoice ? `Netto ${eur(t.net)} + cassa ENPAB 4% ${eur(t.cassa)} = ${eur(t.amount)} (con fattura)` : `${eur(t.amount)} netti, senza fattura né cassa`}</p>}
      {custom && (
        <div className="grid grid-cols-2 items-end gap-3">
          <Field label="Importo netto (€)"><input type="number" step="0.01" min="0" required className="field" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
          <label className="flex items-center gap-2 pb-3 text-sm"><input type="checkbox" checked={f.with_invoice} onChange={(e) => setF({ ...f, with_invoice: e.target.checked })} data-testid="payment-invoice-checkbox" /> Con fattura (+4% ENPAB)</label>
        </div>
      )}
      <div className="flex justify-end"><button className="btn-primary" disabled={saving} data-testid="payment-save-button"><Plus className="h-4 w-4" /> Registra pagamento</button></div>
    </form>
  );
};

const IncomeTable = ({ items, onDelete }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0] bg-white">
    <table className="w-full min-w-[560px] text-left text-sm" data-testid="payments-table">
      <thead className="bg-[#f8fafc]"><tr>{["Data", "Tariffa", "Causale", "Fattura", "Importo", ""].map((h) => <th key={h} className="label-xs px-4 py-3">{h}</th>)}</tr></thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id} className="border-t border-[#e2e8f0]" data-testid={`payment-row-${i.id}`}>
            <td className="px-4 py-3 font-semibold">{fmtDate(i.date)}</td>
            <td className="px-4 py-3">{i.tariff || "—"}</td>
            <td className="px-4 py-3">{i.causale}</td>
            <td className="px-4 py-3">{i.invoiced ? "Sì" : "No"}</td>
            <td className="px-4 py-3 font-bold text-[#3d655b]">{eur(i.amount)}</td>
            <td className="px-2"><button className="btn-ghost text-slate-400 hover:text-rose-600" onClick={() => onDelete(i)} data-testid={`payment-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function PaymentsTab({ rec }) {
  const pid = rec.patient.id;
  const [items, setItems] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [external, setExternal] = useState(false);
  const load = useCallback(() => api.get(`/patients/${pid}/incomes`).then((r) => setItems(r.data)), [pid]);
  useEffect(() => {
    load();
    api.get("/settings/visit-tariffs").then((r) => setTariffs(r.data.tariffs));
    api.get("/studios").then((r) => setExternal(!!r.data.find((s) => s.id === rec.patient.studio_id)?.external));
  }, [load, rec.patient.studio_id]);
  const remove = async (i) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    await api.delete(`/incomes/${i.id}`);
    load();
  };
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold">Pagamenti</h3>
      {external ? (
        <div className="rounded-xl bg-[#fbf8f4] p-3 text-sm text-[#8a6d4f]" data-testid="payment-external-studio-note">Studio esterno: le visite di questo paziente non generano incasso.</div>
      ) : <IncomeForm pid={pid} tariffs={tariffs} onSaved={load} />}
      {items.length ? <IncomeTable items={items} onDelete={remove} /> : <EmptyState icon={Wallet} title="Nessun pagamento registrato" testid="payments-empty" />}
    </div>
  );
}
